import React, { useState, useEffect } from "react";
import axiosInstance from "../api/api.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/AdminSettings.css";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    lateClockInTime: "",
    lateClockOutTime: "",
    fullDayHours: "",
    halfDayHours: "",
    shortDayHours: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [publicHoliday, setPublicHoliday] = useState({
    date: "",
    description: "",
  });
  const [holidays, setHolidays] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddingHoliday, setIsAddingHoliday] = useState(false);
  const [breakHours, setBreakHours] = useState(0);
  const [breakMinutes, setBreakMinutes] = useState(0);


  useEffect(() => {
    fetchSettings();
    fetchHolidays();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get("/admin-settings");
      const data = response.data;

      const breakTime = data.total_break_time || 0;
      setBreakHours(Math.floor(breakTime));
      setBreakMinutes((breakTime % 1) * 60);


      setSettings({
        lateClockInTime: data.late_clock_in_time || "",
        lateClockOutTime: data.late_clock_out_time || "",
        fullDayHours: data.full_day_hours || "",
        halfDayHours: data.half_day_hours || "",
        shortDayHours: data.short_day_hours || "",
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await axiosInstance.get("/holidays");
      setHolidays(response.data);
    } catch (error) {
      console.error("Failed to fetch holidays:", error);
      toast.error("Failed to load holidays");
    }
  };

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async () => {
    setIsUpdating(true);
    try {
      // Ensure totalBreakTime is sent as a number
      const settingsToSave = {
        ...settings,
        totalBreakTime: breakHours + breakMinutes / 60,
      };


      await axiosInstance.post("/admin-settings", settingsToSave);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleHolidayChange = (e) => {
    const { name, value } = e.target;
    setPublicHoliday((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddHoliday = async () => {
    if (!publicHoliday.date || !publicHoliday.description) {
      toast.error("Please fill in both date and description for the holiday");
      return;
    }

    setIsAddingHoliday(true);
    try {
      const response = await axiosInstance.post("/holidays", publicHoliday);

      // Add the new holiday to the list
      setHolidays([...holidays, response.data.holiday]);

      // Reset the form
      setPublicHoliday({ date: "", description: "" });

      toast.success("Holiday added successfully");
    } catch (error) {
      console.error("Failed to add holiday:", error);
      toast.error("Failed to add holiday");
    } finally {
      setIsAddingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await axiosInstance.delete(`/holidays/${holidayId}`);

      // Remove the holiday from the list
      setHolidays(holidays.filter((holiday) => holiday.id !== holidayId));

      toast.success("Holiday deleted successfully");
    } catch (error) {
      console.error("Failed to delete holiday:", error);
      toast.error("Failed to delete holiday");
    }
  };

  const handleUpdateHoliday = async (holidayId, updatedData) => {
    try {
      const response = await axiosInstance.put(
        `/holidays/${holidayId}`,
        updatedData
      );

      // Update the holiday in the list
      setHolidays(
        holidays.map((holiday) =>
          holiday.id === holidayId ? response.data.holiday : holiday
        )
      );

      toast.success("Holiday updated successfully");
    } catch (error) {
      console.error("Failed to update holiday:", error);
      toast.error("Failed to update holiday");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="admin-dashboard-container">
      <ToastContainer />
      <h1>Admin Settings</h1>

      {isLoading ? (
        <div className="loading-spinner">Loading settings...</div>
      ) : (
        <div className="settings-form">
          <h2>Attendance Timings</h2>
          <div className="form-group">
            <label>Late Clock-In Time:</label>
            <input
              type="time"
              name="lateClockInTime"
              value={settings.lateClockInTime}
              onChange={handleSettingChange}
            />
          </div>
          <div className="form-group">
            <label>Total Break Time:</label>
            <div style={{ display: "flex", gap: "10px" }}>
              <select value={breakHours} onChange={(e) => setBreakHours(Number(e.target.value))}>
                {Array.from({ length: 5 }, (_, i) => (
                  <option key={i} value={i}>{i} hour{i !== 1 ? "s" : ""}</option>
                ))}
              </select>
              <select value={breakMinutes} onChange={(e) => setBreakMinutes(Number(e.target.value))}>
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>{m} minutes</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Early Clock-Out Time:</label>
            <input
              type="time"
              name="lateClockOutTime"
              value={settings.lateClockOutTime}
              onChange={handleSettingChange}
            />
          </div>

          <h2>Work Type Timings</h2>
          <div className="form-group">
            <label>Full Day Hours:</label>
            <input
              type="time"
              name="fullDayHours"
              value={settings.fullDayHours}
              onChange={handleSettingChange}
            />
          </div>
          <div className="form-group">
            <label>Half Day Hours:</label>
            <input
              type="time"
              name="halfDayHours"
              value={settings.halfDayHours}
              onChange={handleSettingChange}
            />
          </div>
          <div className="form-group">
            <label>Short Day Hours:</label>
            <input
              type="time"
              name="shortDayHours"
              value={settings.shortDayHours}
              onChange={handleSettingChange}
            />
          </div>

          <button
            className="save-settings-btn"
            onClick={handleSaveSettings}
            disabled={isUpdating}
          >
            {isUpdating ? "Saving..." : "Save Settings"}
          </button>

          <h2>Public Holidays</h2>
          <div className="form-group">
            <label>Holiday Date:</label>
            <input
              type="date"
              name="date"
              value={publicHoliday.date}
              onChange={handleHolidayChange}
            />
          </div>
          <div className="form-group">
            <label>Holiday Description:</label>
            <input
              type="text"
              name="description"
              value={publicHoliday.description}
              onChange={handleHolidayChange}
              placeholder="Enter holiday description"
            />
          </div>
          <button
            className="add-holiday-btn"
            onClick={handleAddHoliday}
            disabled={isAddingHoliday}
          >
            {isAddingHoliday ? "Adding..." : "Add Holiday"}
          </button>

          <div className="holidays-list">
            <h3>Current Holidays</h3>
            {holidays.length === 0 ? (
              <p>No holidays added yet.</p>
            ) : (
              <div className="holidays-grid">
                {holidays.map((holiday) => (
                  <div key={holiday.id} className="holiday-card">
                    <div className="holiday-info">
                      <strong>{formatDate(holiday.date)}</strong>
                      <p>{holiday.description}</p>
                    </div>
                    <div className="holiday-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
